<template>
    <div class="modal-full">
        <div class="modal-header">
            <close-icon v-on:click="closeView"></close-icon>
            <h1>Manage Account
                <FlagIcon class="h-5 w-5 inline" @click="showAccountFlagsView"></FlagIcon>
            </h1>

        </div>

        <div class="modal-body no-pad" v-if="account">
            <div class="flex h-full">
                <div class="account-sidebar h-full">
                    <div class="mb-4 text-center">
                        <p class="text-lg font-light">Account #{{ account.id }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Container</label>
                        <p><a href="#" @click.prevent="handleManageContainer">{{ account.container.name }}</a></p>
                    </div>

                    <div class="mb-3">
                        <label>Subscription</label>
                        <p><a href="#" @click.prevent="handleManageSubscription">{{ packageName }}</a></p>
                    </div>

                    <div class="mb-3" v-if="subscriptionIsPaused">
                        <label>Paused Until</label>
                        <p>{{ pausedUntilFormatted }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Promotion</label>
                        <p><a href="#" @click.prevent="handleManageSubscription">{{ currentPromotion }}</a></p>
                    </div>

                    <div class="mb-3" v-if="account.subscription">
                        <label>Auto-Renew</label>
                        <p>{{ subscriptionIsRenewable ? 'Yes' : 'No' }}</p>
                    </div>

                    <div class="mb-3">
                        <label v-if="subscriptionIsRenewable">Next Billing Date</label>
                        <label v-if="!subscriptionIsRenewable">Expires</label>
                        <p>{{ nextBillingDateFormatted }}</p>
                    </div>

                    <div class="mb-3" v-if="subscriptionIsRenewable">
                        <label>Next Billing Amount</label>
                        <p>{{ nextBillingAmountFormatted }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Saved Payment Method</label>
                        <p><a href="#" @click.prevent="handleNewPaymentMethod">{{ paymentMethod }}</a></p>
                    </div>

                    <div class="mb-3">
                        <label>Credit Balance</label>
                        <p><a href="#" @click.prevent="handleManageCredits">&pound;{{ totalCredit.toFixed(2) }}</a></p>
                    </div>

                    <div class="mb-3">
                        <label>Product Credits</label>
                        <p><a href="#" @click.prevent="handleManageProductCredits">{{
                                account.product_credits.length
                            }}</a></p>
                    </div>

                    <div class="mb-3">
                        <label>Past 12 month sales</label>
                        <p>&pound;{{ lifetimeValue.toLocaleString('eb-GB', {minimumFractionDigits: 2}) }}</p>
                    </div>

                    <div class="mb-3">
                        <label>Tax Rate</label>
                        <p>
                            <TaxRate :account="account" @updated="loadData"/>
                        </p>
                    </div>

                    <div class="mb-3">
                        <label>Has Pro?</label>
                        <p>{{ account.pro ? 'Yes' : 'No' }}</p>
                        <p><a href="#" @click.prevent="handleManagePro">Manage Pro Enrollment</a></p>
                    </div>
                </div>

                <div class="account-body">
                    <div class="p-4 text-green-dark bg-green-lightest border-green-dark border-l-4 mb-4 text-center"
                         v-if="lifetimeValue >= 1000.00">
                        <p>Super customer. Past 12 month revenue exceeds &pound;1,000</p>
                    </div>

                    <div class="alert warning" v-if="uncollectedPayment">
                        <div class="md:flex items-center">
                            <div class="md:w-3/4">
                                <p class="mb-4">Account currently has an uncollected payment.</p>
                            </div>
                            <div class="md:w-1/4 justify-end text-right">
                                <button class="btn-danger muted" type="button" @click="handleCollectPayment">Collect
                                    Payment
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="preselections alert warning" v-if="userCheckoutDetails">
                        <div class="md:flex items-center">
                            <div class="md:w-3/4">
                                <p class="mb-4">Account currently has a forced checkout pending for the following:</p>

                                <p v-if="userCheckoutDetails.package">
                                    Selected Package: <strong>{{ userCheckoutDetails.package_name }}</strong>
                                </p>
                                <p v-if="userCheckoutDetails.product">
                                    Selected Product: <strong>{{ userCheckoutDetails.product_name }}</strong>
                                </p>
                                <p v-if="userCheckoutDetails.cost">
                                    Cost (Gross): <strong>&pound;{{ userCheckoutDetails.cost.toFixed(2) }}</strong><br>
                                </p>
                                <p v-if="userCheckoutDetails.promotion">
                                    Selected Promotion: <strong>{{ userCheckoutDetails.promotion_name }}</strong>
                                </p>
                            </div>
                            <div class="md:w-1/4 justify-end text-right">
                                <button class="btn-danger muted" @click.prevent="removeUserCheckout">Remove</button>
                            </div>
                        </div>
                    </div>

                    <div class="p-4 text-orange-600 bg-orange-100 border-orange-600 border-l-4 mb-4 text-center"
                         v-if="unusedMediaDatabaseLicences > 0">
                        <p>Account has {{ unusedMediaDatabaseLicences }} unused Media Database licences</p>
                    </div>

                    <div class="p-4 text-red-600 bg-red-100 border-red-600 border-l-4 mb-4 text-center"
                         v-if="unusedMediaDatabaseLicences < 0">
                        <p>Account has too many Media Database licences</p>
                    </div>

                    <div class="alert warning" v-if="account.pending_subscription_change">
                        <div class="md:flex items-center">
                            <div class="md:w-3/4">
                                <p>Subscription will be changed to <strong>{{
                                        account.pending_subscription_change.name
                                    }} (&pound;{{ account.pending_subscription_change.cost.toFixed(2) }} + VAT)</strong>
                                    at the next renewal.</p>
                            </div>

                            <div class="md:w-1/4 justify-end text-right">
                                <button class="btn-danger muted" @click.prevent="removePendingSubscriptionChange">
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="alert warning" v-if="account.pending_promotion_change">
                        <div class="md:flex items-center">
                            <div class="md:w-3/4">
                                <p>Promotion <strong>{{ account.pending_promotion_change.code }}</strong> will be
                                    applied at the next renewal.</p>
                            </div>

                            <div class="md:w-1/4 justify-end text-right">
                                <button class="btn-danger muted" @click.prevent="removePendingPromotionChange">Cancel
                                </button>
                            </div>
                        </div>
                    </div>

                    <div id="account-users" class="mb-16">
                        <p class="text-lg font-light mb-6">Users</p>
                        <div class="results-list">
                            <div class="results-row">
                                <div class="results-head" data-width="10%">ID</div>
                                <div class="results-head" data-width="90%">Email</div>
                            </div>
                            <template v-if="account.users">
                                <div class="results-row" v-for="(result, index) in account.users" :key="index">
                                    <div class="results-cell" data-width="10%">{{ result.id }}</div>
                                    <div class="results-cell" data-width="50%">
                                        {{ result.email }}
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512"
                                             class="text-blue h-4 w-4 ml-1"
                                             v-if="userHasMediaDatabaseLicence(result)">
                                            <path fill="currentColor"
                                                  d="M448 358.4V25.6c0-16-9.6-25.6-25.6-25.6H96C41.6 0 0 41.6 0 96v320c0 54.4 41.6 96 96 96h326.4c12.8 0 25.6-9.6 25.6-25.6v-16q0-9.6-9.6-19.2c-3.2-16-3.2-60.8 0-73.6q9.6-4.8 9.6-19.2zM240 64a64 64 0 1 1-64 64 64 64 0 0 1 64-64zM128 281.6c0-31.81 30.09-57.6 67.2-57.6h5a103.25 103.25 0 0 0 79.7 0h4.95c37.11 0 67.2 25.79 67.2 57.6v19.2c0 10.61-10 19.2-22.4 19.2H150.4c-12.4 0-22.4-8.6-22.4-19.2zM380.8 448H96c-19.2 0-32-12.8-32-32s16-32 32-32h284.8z"/>
                                        </svg>
                                    </div>
                                    <div class="results-cell flex" data-width="40%">
                                        <button class="btn-primary muted text-center mr-2" type="button"
                                                @click="handleSelectUser(result.id)">View
                                        </button>
                                        <button class="btn-tertiary muted text-center mr-2" type="button"
                                                @click="handlePermissions(result.id)">Permissions
                                        </button>
                                        <button class="btn-tertiary muted text-center" type="button"
                                                @click="handleUserProductLicences(result.id)">Product Licences
                                        </button>
                                    </div>
                                </div>
                            </template>
                            <div class="results-row" v-if="!account.users">
                                <div class="results-span text-center" data-width="100%">
                                    <p>No results found</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div id="account-businesses" class="mb-16">
                        <p class="text-lg font-light my-6">Businesses
                            <button @click="handleCreateBusiness"
                                    class="btn-primary small muted text-center mb-1 text-sm float-right">New Business
                            </button>
                        </p>
                        <div class="inline-warning"
                             v-if="!Array.isArray(account.businesses) || !account.businesses.length">
                            There are currently no businesses assigned to this account.
                        </div>
                        <div class="results-list">
                            <div class="results-row">
                                <div class="results-head" data-width="10%">ID</div>
                                <div class="results-head" data-width="90%">Name</div>
                            </div>
                            <template v-if="account.businesses">
                                <div class="results-row" v-for="(result, index) in account.businesses" :key="index"
                                     @click="handleViewBusinesses(result.id)">
                                    <div class="results-cell" data-width="10%">{{ result.id }}</div>
                                    <div class="results-cell" data-width="90%">{{ result.name }}</div>
                                </div>
                            </template>
                            <div class="results-row" v-if="!account.businesses">
                                <div class="results-span text-center" data-width="100%">
                                    <p>No results found</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div id="orders">
                        <p class="text-lg font-light my-6">Orders
                            <button @click="toggleIncompleteOrders"
                                    class="btn-primary small muted text-center mb-1 text-sm float-right">
                                {{ toggleIncompleteOrdersMessage }}
                            </button>
                        </p>
                        <div class="results-list">
                            <div class="results-row">
                                <div class="results-head" data-width="10%">ID</div>
                                <div class="results-head" data-width="20%">Date</div>
                                <div class="results-head" data-width="20%">Value</div>
                                <div class="results-head" data-width="40%">Description</div>
                                <div class="results-head" data-width="10%">Status</div>
                            </div>
                            <template v-if="account.orders">
                                <div class="results-row" v-for="(order, index) in orderList" :key="index"
                                     @click="handleViewOrder(order)">
                                    <div class="results-cell" data-width="10%">{{ order.id }}</div>
                                    <div class="results-cell" data-width="20%">
                                        {{ formatDateRelative(order.created_at) }}
                                    </div>
                                    <div class="results-cell" data-width="20%">{{
                                            formatOrderValue(order.gross_cost)
                                        }}{{ order.credits.length > 0 ? '*' : '' }}
                                    </div>
                                    <div class="results-cell" data-width="40%">{{ order.description }}<span
                                        v-if="order.created_from !== null" class="ml-1">(Renewal)</span></div>
                                    <div class="results-cell text-red-500 font-bold" data-width="10%"
                                         v-if="order.complete === null">Incomplete
                                    </div>
                                    <div class="results-cell text-green font-bold" data-width="10%"
                                         v-if="order.complete !== null">Complete
                                    </div>
                                </div>
                            </template>
                            <div class="results-row" v-if="!account.orders">
                                <div class="results-span text-center" data-width="100%">
                                    <p>No results found</p>
                                </div>
                            </div>
                        </div>

                        <p class="text-xs text-gray-500mt-1">* indicates payment for the order was for a different
                            amount.</p>
                    </div>
                </div>

                <div class="account-activity">
                    <p class="text-lg font-light">Groups Membership</p>
                    <ul class="list-reset mb-4">
                        <li v-for="group in account.account_groups" :key="group.id" class="pb-1">
                            {{ group.name }}
                        </li>
                        <li v-if="account.account_groups.length === 0">
                            (None)
                        </li>
                        <li><a href="#" @click.prevent="handleManageGroupMembership">Manage</a></li>
                    </ul>

                    <p class="text-lg font-light">Features</p>
                    <p class="text-xs italic">Italic features are inherited</p>
                    <ul class="list-reset" v-if="featureFlags">
                        <template v-if="featureFlags.features">
                            <li v-for="flag in featureFlags.features" :key="flag" class="pb-1">
                                {{ flag }}
                            </li>
                        </template>
                        <template v-if="featureFlags.inherited">
                            <li v-for="flag in featureFlags.inherited" :key="flag"
                                class="pb-1 italic">
                                {{ flag }}
                            </li>
                        </template>
                    </ul>

                    <div class="mt-4">
                        <p class="text-lg font-light">Acquisition</p>
                        <p>
                            <ChannelAttribution :account="account"/>
                        </p>
                    </div>
                </div>
            </div>
        </div>

        <div class="modal-footer">
            <div class="flex">
                <div class="left text-left">
                    <div class="actions" v-click-away="hideActions">
                        <div class="actions-menu" v-show="actionsShown">
                            <button class="btn-primary muted" type="button" @click="handleManageSubscription">Manage
                                Subscription
                            </button>
                            <button class="btn-primary muted" type="button" @click="handleProductOrder">New Product
                                Order
                            </button>
                            <button class="btn-primary muted" type="button" @click="handleManageFeatures">Manage
                                Features
                            </button>
                            <button class="btn-primary muted" type="button" @click="handleManageGroupMembership">Account
                                Groups
                            </button>
                            <button class="btn-danger muted" type="button" @click="handleDeleteAccount">Delete Account
                            </button>
                            <button class="btn-tertiary" type="button" @click="handleVerifyAccount"
                                    v-if="verificationPending">Verify Account
                            </button>
                        </div>
                        <button class="btn-primary muted" type="button" :data-open="actionsShown"
                                @click="actionsShown = !actionsShown">
                            <ChevronUpIcon class="h-6 w-6 inline" />
                            Actions
                        </button>
                    </div>
                </div>

                <div class="text-center">
                    <NotesDrawerButton :account="account" v-if="account"/>
                </div>

                <div class="right text-right">
                    <button class="btn-tertiary muted" type="button" @click="closeView">Close</button>
                    <button class="btn-primary" type="button" @click="handleAccountUpdate">Save</button>
                </div>
            </div>
        </div>
        <router-view v-slot="{ Component }">
            <transition name="drop-in" mode="in-out">
                <component :is="Component"></component>
            </transition>
        </router-view>
    </div>
</template>

<script>
import CardPayment from '../../CardPayment'
import ChannelAttribution from './ChannelAttribution'
import TaxRate from './TaxRate'
import NotesDrawerButton from './Notes/NotesDrawerButton'
import {ChevronUpIcon, FlagIcon} from "@heroicons/vue/24/solid"

export default {
    name: 'AccountView',
    props: {
        //
    },
    components: {
        TaxRate,
        NotesDrawerButton,
        CardPayment,
        ChannelAttribution,
        FlagIcon,
        ChevronUpIcon
    },
    data() {
        return {
            account: null,
            totalCredit: null,
            orderCredit: {
                value: 0,
                description: ''
            },
            flags: {
                rate_limit: null,
            },
            containers: [],
            actionsShown: false,
            showIncompleteOrders: false,
            verificationPending: false,
            featureFlags: false,
            uncollectedPayment: false,
            userCheckoutObject: null,
        }
    },
    computed: {
        /**
         * Return the current package name as a string
         *
         * @return {String}
         */
        packageName() {
            if (this.account.subscription) {
                return this.account.subscription.package.name + (this.subscriptionIsPaused ? ' (Paused)' : '')
            }

            return 'PAYG'
        },
        /**
         * Determine if the current subscription is paused
         *
         * @return {Boolean}
         */
        subscriptionIsPaused() {
            return this.account.subscription &&
                this.account.subscription.paused_until &&
                moment(this.account.subscription.paused_until).isAfter(moment())
        },
        /**
         * Determine if the current subscription is set to auto renew.
         *
         * @return {Boolean}
         */
        subscriptionIsRenewable() {
            return this.account.subscription &&
                this.account.subscription.auto_renew
        },
        /**
         * Return a formatted string of the next billing date
         *
         * @return {String}
         */
        nextBillingDateFormatted() {
            if (!this.account) {
                return ''
            }

            if (!this.account.subscription || this.account.subscription.auto_renew === 0) {
                return 'N/A'
            }

            return this.formatDateShort(this.account.subscription.expires)
        },
        /**
         * Return a formatted string of the next billing amount
         *
         * @return {String}
         */
        nextBillingAmountFormatted() {
            if (!this.account) {
                return ''
            }

            if (!this.account.subscription || !this.account.subscription.order) {
                return 'N/A'
            }

            return '£' + this.account.subscription.order.recurring_gross_cost.toFixed(2)
        },
        /**
         * Return a formatted string of the paused until date
         *
         * @return {String}
         */
        pausedUntilFormatted() {
            if (!this.account || !this.account.subscription || !this.account.subscription.paused_until) {
                return ''
            }

            return this.formatDateShort(this.account.subscription.paused_until)
        },
        /**
         * Return a string detailing the current applied promotion
         *
         * @return {String}
         */
        currentPromotion() {
            if (!this.account || !this.account.subscription || !this.account.subscription.order) {
                return '(None)'
            }

            return this.account.subscription.order.promotion ?
                this.account.subscription.order.promotion.code :
                '(None)'
        },
        /**
         * Return the friendly name for the current saved payment method
         *
         * @return {String}
         */
        paymentMethod() {
            if (!this.account || !this.account.subscription || !this.account.subscription.order) {
                return '(None)'
            }

            let billing = this.account.subscription.order.order_billing

            if (!billing || !billing.provider) {
                return '(None)'
            }

            return '(' + billing.provider.name + ') ' + billing.friendly_name
        },
        /**
         * Compute the message to display for the toggle incomplete orders link
         *
         * @return {String}
         */
        toggleIncompleteOrdersMessage() {
            if (this.showIncompleteOrders) {
                return 'Hide Incomplete Orders'
            }

            return 'Show Incomplete Orders'
        },
        /**
         * Return the total value of all completed orders for this Account
         *
         * @return {Number}
         */
        lifetimeValue() {
            if (!this.account) {
                return 0
            }

            return this.account.orders.reduce((carry, order) => {
                if (order.complete === null || moment(order.complete).isBefore(moment().subtract(1, 'years'))) {
                    return carry
                }

                return parseFloat(carry) + parseFloat(order.net_cost)
            }, 0.0)
        },
        userCheckoutDetails() {
            if (!this.account.flags || (!this.account.flags['user_checkout'] && !this.userCheckoutObject)) {
                return null
            }

            if (this.userCheckoutObject) {
                let obj = {
                    cost: this.userCheckoutObject.gross_cost,
                }

                if (_.endsWith(this.userCheckoutObject.orderable_type, 'Package')) {
                    obj.package = this.userCheckoutObject.orderable
                    obj.package_name = this.userCheckoutObject.orderable.name
                }

                if (_.endsWith(this.userCheckoutObject.orderable_type, 'Product')) {
                    obj.product = this.userCheckoutObject.orderable
                    obj.product_name = this.userCheckoutObject.orderable.name
                }

                return obj
            }

            return JSON.parse(this.account.flags['user_checkout'])
        },

        orderList() {
            if (!this.showIncompleteOrders) {
                return this.account.orders.filter(order => order.complete !== null)
            }

            return this.account.orders
        },
        /**
         * Return the number of unused media database licences.
         *
         * @return {Number}
         */
        unusedMediaDatabaseLicences() {
            let enablement = this.account.subscription?.enablements?.find(
                e => e.permission === 'media-database'
            )

            if (!enablement) {
                return 0
            }

            let licences = this.account.users
                .flatMap(user => user.productlicences)
                .filter(licence => licence.product === 'media-database')

            return enablement.value - licences.length
        },
    },
    mounted() {
        this.loadData()
        this.setMetaTitle('Account Management | JournoLink Control')
    },
    beforeRouteUpdate(to, from, next) {
        this.$route.params.accountId = to.params.accountId
        this.loadData()
        next()
    },
    methods: {
        loadData() {
            axios.get(route('accounts.show', this.$route.params.accountId))
                .then(response => {
                    this.account = response.data.account
                    this.initialiseAccountFlags(this.account.flags, response.data.allFlags)
                    this.credits = response.data.account.order_credits.map(credit => credit.value)
                    this.containers = response.data.containers
                    this.totalCredit = response.data.orderCreditBalance
                    this.verificationPending = this.account.flags.hasOwnProperty('pending_verification')
                    this.featureFlags = response.data.featureFlags
                    this.uncollectedPayment = response.data.uncollectedPayment
                    this.loadUserCheckoutObject()
                })
                .catch(e => {
                    console.error({e})
                })
        },
        /**
         * Hide the actions box
         */
        hideActions() {
            this.actionsShown = false
        },
        /**
         * Emit the close event to the parent
         *
         * @return {void}
         */
        closeView() {
            this.setMetaTitle('List Users | JournoLink Control')
            this.$root.goBack()
        },
        /**
         * Return the local user name from an email address
         *
         * @param  {String} email
         * @return {String}
         */
        getUserFromEmail(email) {
            return email.substring(0, email.lastIndexOf("@"))
        },
        /**
         * Handle a user selection
         *
         * @return {void}
         */
        handleSelectUser(userId) {
            this.$router.push({name: 'users.show', params: {userId: userId}})
        },
        /**
         * View business details
         *
         * @return {void}
         */
        handleViewBusinesses(businessId) {
            this.$router.push({name: 'business.show', params: {businessId: businessId}})
        },
        /**
         * Create Business
         *
         * @return {void}
         */
        handleCreateBusiness() {
            this.$router.push({name: 'business.create'})
        },
        /**
         * Manage Subscription
         *
         * @return {void}
         */
        handleManageSubscription() {
            this.$router.push({name: 'subscriptions.show', params: {accountId: this.account.id}})
        },
        /**
         * Manage Feature Flags
         *
         * @return {void}
         */
        handleManageFeatures() {
            this.$router.push({name: 'account.features', params: {accountId: this.account.id}})
        },
        /**
         * Manage Group Membership
         *
         * @return {void}
         */
        handleManageGroupMembership() {
            this.$router.push({name: 'account.groups.show', params: {accountId: this.account.id}})
        },
        /**
         * Handle a request to start a new product order
         *
         * @return  {void}
         */
        handleProductOrder() {
            this.$router.push({name: 'order.create', params: {account: this.account}})
        },
        /**
         * Handle a click event to manage the Account Container
         *
         * @return {void}
         */
        handleManageContainer() {
            this.$router.push({name: 'account.container', params: {account: this.account, containers: this.containers}})
        },
        /**
         * Handle a click event to manage the Account Container
         *
         * @return {void}
         */
        handleManagePro() {
            this.$router.push({name: 'account.pro', params: {account: this.account}})
        },
        /**
         * Handle a request to view the selected Order
         *
         * @return {void}
         */
        handleViewOrder(order) {
            this.$router.push({name: 'order.show', params: {orderId: order.id, order: order}})
        },
        /**
         * Handle a request to change the account payment method
         *
         * @return {void}
         */
        handleNewPaymentMethod() {
            this.$router.push({name: 'paymentmethod.show', params: {accountId: this.account.id}})
        },
        /**
         * Handle a click event to manage the Account credits
         *
         * @return {void}
         */
        handleManageCredits() {
            this.$router.push({name: 'account.credits', params: {accountId: this.account.id}})
        },
        /**
         * Handle a click event to manage the Account product credits
         *
         * @return {void}
         */
        handleManageProductCredits() {
            this.$router.push({
                name: 'account.productcredits',
                params: {accountId: this.account.id, account: this.account}
            })
        },
        /**
         * Handle credit allocation
         */
        handleAccountUpdate() {
            axios.post(route('accounts.update', this.$route.params.accountId), {
                orderCreditValue: this.orderCredit.value,
                orderCreditDesc: this.orderCredit.description,
                flags: this.flags,
            }).then(response => {
                this.$bus.$emit('alert.success', 'Update Success')
                this.totalCredit += this.orderCredit.value
                this.orderCredit.value = 0
                this.orderCredit.description = ''
            })
                .catch(e => {
                    this.$bus.$emit('alert.danger', 'Update Failed')
                })
        },
        /**
         * Initialise all account flags
         */
        initialiseAccountFlags(accountFlags, allFlags) {
            for (let flag in allFlags) {
                this.flags[allFlags[flag]] = accountFlags[allFlags[flag]]
            }
        },
        /**
         * Show the Account Flags View
         */
        showAccountFlagsView() {
            this.$router.push({name: 'account.flags.show', params: {accountId: this.account.id, flags: this.flags}})
        },
        /**
         * Format an order value for output
         *
         * @param  {Float} value
         * @return {String}
         */
        formatOrderValue(value) {
            if (value < 0) {
                return '(£' + Math.abs(value).toFixed(2) + ')'
            }

            return '£' + Math.abs(value).toFixed(2)
        },
        /**
         * Toggle whether incomplete orders should be displayed in the Order list
         *
         * @return {void}
         */
        toggleIncompleteOrders() {
            this.showIncompleteOrders = !this.showIncompleteOrders
        },
        /**
         * Handle an [Escape] keypress
         *
         * @return {void}
         */
        onEscapeKeypress() {
            this.closeView()
        },
        /**
         *
         */
        handleDeleteAccount() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'This will delete the account, all account users, and businesses. This action is irreversible.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#E3342F',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.delete(route('accounts.delete', this.$route.params.accountId), {}).then(response => {
                            this.$bus.$emit('alert.success', 'Account Deleted')
                            this.$router.push({name: 'users.list'})
                        }).catch(e => {
                            console.error({e})
                            this.$bus.$emit('alert.danger', 'Account Not Deleted')
                        })
                    }
                })
        },
        /**
         *
         */
        handleVerifyAccount() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'This will verify the account giving the user full access',
                icon: 'question',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                confirmButtonColor: '#2980b9',
                cancelButtonText: 'No, go back'
            })
                .then((selected) => {
                    if (selected.value) {
                        axios.post(route('accounts.update', this.$route.params.accountId), {
                            flags: {pending_verification: this.verificationPending = null},
                        }).then(response => {
                            this.$bus.$emit('alert.success', 'Verification Success')
                            this.flags.pending_verification = this.verificationPending
                        })
                            .catch(e => {
                                this.$bus.$emit('alert.danger', 'Verification Failed')
                            })
                    }
                })
        },
        /**
         * View permissions with this user
         */
        handlePermissions(userId) {
            this.$router.push({
                name: 'account.users.permissions',
                params: {accountId: this.account.id, userId: userId}
            })
        },
        /**
         * Open the Product Licences tab for the given User.
         *
         * @param  {Number} userId
         * @return {void}
         */
        handleUserProductLicences(userId) {
            this.$router.push({name: 'users.productlicences', params: {userId: userId}})
        },

        getFlagsForSave() {
            let returnFlags = {}
            for (let flag in this.keyedFlags) {
                returnFlags[this.keyedFlags[flag].key] = this.keyedFlags[flag].value
            }

            return returnFlags
        },
        /**
         * Remove the forced User Checkout from this Account.
         *
         * @return {void}
         */
        removeUserCheckout() {
            if (confirm('Are you sure?')) {
                this.flags['user_checkout'] = null
                this.flags['user_checkout_order'] = null

                this.updateAccountFlags(this.flags)
            }
        },

        /**
         * Remove the planned subscription package change from this Account.
         *
         * @return {void}
         */
        removePendingSubscriptionChange() {
            if (confirm('Are you sure?')) {
                this.flags['pending_subscription_change'] = null

                this.updateAccountFlags(this.flags)
            }
        },

        /**
         * Remove the planned promotion application from this Account.
         *
         * @return {void}
         */
        removePendingPromotionChange() {
            if (confirm('Are you sure?')) {
                this.flags['pending_promotion_change'] = null

                this.updateAccountFlags(this.flags)
            }
        },

        /**
         * Update the flags for the Account.
         *
         * @param  {Object} flags
         * @return {void}
         */
        updateAccountFlags(flags) {
            axios.post(route('accounts.update', this.$route.params.accountId), {
                flags,
            }).then(response => {
                this.$bus.$emit('alert.success', 'Update Success')
                this.loadData()
            })
                .catch(e => {
                    this.$bus.$emit('alert.danger', 'Update Failed')
                })
        },

        handleCollectPayment() {
            this.alert.fire({
                title: 'Are you sure?',
                text: 'This would attempt to collect a payment from the account.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Yes, continue',
                cancelButtonText: 'No, go back'
            }).then((selected) => {
                if (selected.value) {
                    axios.post(route('accounts.collect_payment', this.$route.params.accountId))
                        .then(response => {
                            this.$bus.$emit('alert.success', 'Payment Collected Successfully')
                            this.loadData()
                        })
                        .catch(e => {
                            this.$bus.$emit('alert.danger', 'Payment Collection Failed')
                        })
                }
            })
        },
        /**
         * Load the order related to the user checkout
         *
         * @return {void}
         */
        loadUserCheckoutObject() {
            this.userCheckoutObject = null

            if (this.account.flags['user_checkout_order']) {
                axios.get(route('orders.show', this.account.flags['user_checkout_order']))
                    .then(response => {
                        this.userCheckoutObject = response.data
                    })
            }
        },
        /**
         * Determine whether the given User object has a valid licence to the media database.
         *
         * @param  {Object} user
         * @return {Boolean}
         */
        userHasMediaDatabaseLicence(user) {
            let licences = user.productlicences?.filter(licence => licence.product === 'media-database')

            return licences && licences.length > 0
        },
    }
}
</script>

<style lang="less">
svg.flags {
    cursor: pointer;
    position: absolute;
    top: 15px;
    right: 40px;
    color: #888888;

    &:hover {
        color: #222222;
    }
}

.account-sidebar {
    padding: 15px;
    width: 250px;
    font-size: 0.9rem;
    background: #f6f6f6;

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

.account-body {
    #account-users {
        button {
            width: 100%;
            padding: 0.25rem 0.75rem;
        }
    }
}

.account-activity {
    width: 250px;
    font-size: 0.9rem;
    padding: 15px;
    background: #f6f6f6;

    label {
        font-weight: bold;
        font-size: 0.8em;
        color: #999999;
    }

    p {
        margin-bottom: 0.5em;
    }
}

p.info {
    margin-top: 10px;
    color: #8795a1;
    font-size: smaller;
}

.actions-menu {
    position: fixed;
    left: 0;
    bottom: 72px;
    padding: 15px;
    background: #dde0e2;

    > button {
        margin-bottom: 10px;
        display: block;
        width: 100%;
    }
}
</style>
